declare let accounting: accounting.Static;

import Utilities from '@/Utility/Utilities';
import * as Interfaces from '#/CoreFrameInterfaces';
import ResourceManager from '@/Management/ResourceManager';
import RADApplication from '@/Management/RADApplication';
import * as CoreFrameDTOs from "coreframetypings/dto";

export default class CurrencyHelper {

    private static readonly maxCurrencyDecimals = 5;

    public static toCurrencyString(value: string|number, currencyId: string): string {
        let language = ResourceManager.getInstance().getLanguage();

        let currency = _.find(RADApplication.getInstance().configuration.currencies, (that: any) => {
            return that.isO4217NumberCode === currencyId;
        });

        if (value == null || currency == null || _.isString(value)) {
            return '-';
        }

        let thousandSep = language.currencyPattern.replace(/#/g, '')[0];
        let decimalSep = language.currencyPattern.replace(/#/g, '')[1];
        let precision = currency.scale;
        let currencyName = currency.shortname;

        return accounting.formatMoney(value, currencyName, precision, thousandSep, decimalSep, '%v %s');
    }

    /**
     * Wandelt einen CurrencyWert in das Format des Servers um
     * @param value
     */
    public static toServerValue(value: number): number {
        if (Utilities.isNullOrUndefined(value)) {
            return null;
        }

        return Math.round(value * 100000);
    }

    /**
     * Wandelt einen CurrencyWert in das Format des Clients um
     * @param value 
     */
    public static toClientValue(value: number): number {
        if (Utilities.isNullOrUndefined(value)) {
            return null;
        }

        return value / 100000;
    }

    /**
     * Gibt für eine Sprache (per ISO-Code) die jeweilige CurrencyPatternInfo zurück
     * @param languageIso ISO-Code der Sprache
     */
    public static getCurrencyPatternInfo(languageIso: string): Interfaces.ICurrencyPatternInfo {
        const language = ResourceManager.getInstance().getLanguage(languageIso);
        if (Utilities.isNullOrUndefined(language))
            return null;

        const pattern = language.currencyPattern;

        let decimals = 0;
        let decimalSeparator = ',';
        let groupingSeparator = '.';

        let countingDecimals = true;

        const length = pattern.length;
        for (let i = length - 1; i >= 0; i--) {
            const current = pattern[i];
            if (countingDecimals) {
                if (current === '#')
                    decimals++;
                else {
                    countingDecimals = false;
                    decimalSeparator = current;
                }
            }
            else {
                if (current !== '#') {
                    groupingSeparator = current;
                    break;
                }
            }
        }

        const currencyPatternParsed = (!countingDecimals)
            && (decimalSeparator !== groupingSeparator)
            && decimals > 0
            && decimals <= CurrencyHelper.maxCurrencyDecimals;
        if (!currencyPatternParsed)
            throw new Error(`Currency pattern has wrong format: ${pattern}`);

        return {
            pattern: pattern,
            decimals: decimals,
            decimalSeparator: decimalSeparator,
            groupingSeparator: groupingSeparator
        };
    }

    /**
     * Gibt alle verfügbaren Währungen zurück
     */
    public static getAllCurrencies(): CoreFrameDTOs.ICurrencyDTO[] {
        return _.values(RADApplication.getInstance().configuration.currencies);
    }

    /**
     * Gibt die Währung anhand des ISO-Codes zurück
     * @param isoCode
     */
    public static getCurrencyByIsoCode(isoCode: number): CoreFrameDTOs.ICurrencyDTO {
        const filtered = CurrencyHelper.getAllCurrencies().filter(c => c.isO4217NumberCode === isoCode);
        if (filtered.length !== 1)
            return null;

        return filtered[0];
    }

    /**
     * Gibt alle direkten Wechselkurse für eine Währung innerhalb einer Wechselkursgruppe zurück
     * @param currencyExchangeGroup
     * @param currencyId
     */
    public static getCurrencyExchangeRatesForCurrency(currencyExchangeGroup: string, currencyId: number): CoreFrameDTOs.ICurrencyExchangeRateDTO[] {
        const group = RADApplication.getInstance().configuration.currencyExchangePaths[currencyExchangeGroup];
        if (Utilities.isNullOrUndefined(group)) {
            return [];
        }
        let rates = _.values<CoreFrameDTOs.ICurrencyExchangeRateDTO>(group[currencyId]);
        if (Utilities.isNullOrUndefined(rates))
            rates = [];

        return rates;
    }

    /**
     * Gibt alle Währungen zurück, für die ein direkter Wechselkurs der Eingangswährung existiert
     * @param isoCode
     * @param sortMember
     */
    public static getAllExchangeCurrenciesForCurrency(isoCode: number, sortMember: string[] = null): CoreFrameDTOs.ICurrencyDTO[] {
        let currencies: CoreFrameDTOs.ICurrencyDTO[] = [];
        let ids: number[] = [isoCode];
        const currencyExchangeGroup = '-empty-';
        
        CurrencyHelper.getCurrencyExchangeRatesForCurrency(currencyExchangeGroup, isoCode).forEach((item) => {
            if (!_.contains(ids, item.Target)) {
                ids.push(item.Target);
            }

        });

        CurrencyHelper.getAllCurrencies().forEach((currency) => {
            if (_.contains(ids, currency.isO4217NumberCode) && (currency.isActive || currency.isO4217NumberCode === ~~isoCode)) {
                currencies.push(currency);
            }
        });

        if (sortMember == null)
            return currencies;

        let chain = _.chain(currencies);
        if (sortMember != null && sortMember.length > 0) {
            sortMember.forEach(sortMember => {
                chain = chain.sortBy(sortMember);
            });
        }
        const sortedCurrencies = chain.value();
        return sortedCurrencies;
    }

    /**
     * Formatiert einen Währungswert
     * @param value
     * @param isoCode
     */
    public static formatCurrency(value: number, isoCode: number): string {
        const clientValue = CurrencyHelper.toClientValue(value);
        const currency = CurrencyHelper.getCurrencyByIsoCode(isoCode);
        if (currency == null) {
            return `${Utilities.toLocaleNumber(clientValue)}`;
        }

        return `${Utilities.toLocaleNumber(clientValue)} ${currency.shortname}`;
    }
}